import type { Task } from '@/services/task.service';
import {
  eachDayOfInterval,
  startOfDay,
  differenceInCalendarDays,
  max,
  min,
  isSameDay,
  addDays,
} from 'date-fns';

export type TaskHealthStatus = 'on_track' | 'at_risk' | 'critical' | 'overdue' | 'completed';
export type PlannerPeriod = 'week' | '2-weeks' | 'month';

export interface TaskHealth {
  status: TaskHealthStatus;
  message: string;
  remainingHours: number;
  availableHours: number;
  overlappingHoursFromOtherTasks: number;
}

export interface DayCapacity {
  date: Date;
  isWorkingDay: boolean;
  totalCapacity: number;
  usedHours: number;
  utilizationPercentage: number;
}

export interface PlannerTask extends Task {
  health: TaskHealth;
  row: number;
  startColumn: number;
  endColumn: number;
  spanDays: number;
}

const DAY_NAME_TO_NUMBER: Record<string, number> = {
  sunday: 0,
  monday: 1,
  tuesday: 2,
  wednesday: 3,
  thursday: 4,
  friday: 5,
  saturday: 6,
};

const DEFAULT_WORKING_DAYS = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday'];

export const isWorkingDay = (date: Date, workingDays: string[] = DEFAULT_WORKING_DAYS): boolean => {
  const dayNumber = date.getDay();
  const workingDayNumbers = workingDays.map(d => DAY_NAME_TO_NUMBER[d.toLowerCase()]);
  return workingDayNumbers.includes(dayNumber);
};

export const countWorkingDays = (
  startDate: Date,
  endDate: Date,
  workingDays: string[] = DEFAULT_WORKING_DAYS
): number => {
  if (startDate > endDate) return 0;

  const allDays = eachDayOfInterval({ start: startDate, end: endDate });
  return allDays.filter(day => isWorkingDay(day, workingDays)).length;
};

export const calculateProRatedHoursForPeriod = (
  task: { start_date: string; end_date: string; estimated_hours: number | null },
  periodStart: Date,
  periodEnd: Date,
  workingDays: string[] = DEFAULT_WORKING_DAYS
): number => {
  if (!task.estimated_hours) return 0;

  const taskStart = startOfDay(new Date(task.start_date));
  const taskEnd = startOfDay(new Date(task.end_date));

  const overlapStart = max([taskStart, periodStart]);
  const overlapEnd = min([taskEnd, periodEnd]);

  if (overlapStart > overlapEnd) return 0;

  const overlapWorkingDays = countWorkingDays(overlapStart, overlapEnd, workingDays);
  const taskTotalWorkingDays = countWorkingDays(taskStart, taskEnd, workingDays);

  if (taskTotalWorkingDays <= 0) return task.estimated_hours;

  return task.estimated_hours * (overlapWorkingDays / taskTotalWorkingDays);
};

export const calculateTaskHealth = (
  task: Task,
  allUserTasks: Task[],
  workingDays: string[] = DEFAULT_WORKING_DAYS,
  dailyHours: number = 8
): TaskHealth => {
  const today = startOfDay(new Date());
  const endDate = task.end_date ? startOfDay(new Date(task.end_date)) : today;
  const startDate = task.start_date ? startOfDay(new Date(task.start_date)) : today;

  // Completed tasks
  if (task.phase_status === 'done') {
    return {
      status: 'completed',
      message: 'Task completed',
      remainingHours: 0,
      availableHours: 0,
      overlappingHoursFromOtherTasks: 0,
    };
  }

  // Overdue tasks
  if (today > endDate) {
    const remainingHours = Math.max(0, (task.estimated_hours || 0) - (task.actual_hours || 0));
    return {
      status: 'overdue',
      message: `Overdue by ${differenceInCalendarDays(today, endDate)} days`,
      remainingHours,
      availableHours: 0,
      overlappingHoursFromOtherTasks: 0,
    };
  }

  // Calculate remaining work
  const remainingHours = Math.max(0, (task.estimated_hours || 0) - (task.actual_hours || 0));

  // Calculate available working hours from today (or start date if in future) to end date
  const effectiveStart = max([today, startDate]);
  const availableWorkingDays = countWorkingDays(effectiveStart, endDate, workingDays);
  const totalAvailableHours = availableWorkingDays * dailyHours;

  // Find overlapping tasks (excluding current task)
  const overlappingTasks = allUserTasks.filter(t =>
    t.id !== task.id &&
    t.phase_status !== 'done'
  ).filter(t => {
    if (!t.start_date || !t.end_date) return false;
    const tStart = startOfDay(new Date(t.start_date));
    const tEnd = startOfDay(new Date(t.end_date));
    // Check if task overlaps with remaining period of current task
    return tStart <= endDate && tEnd >= effectiveStart;
  });

  // Calculate hours from overlapping tasks that compete for the same time
  const overlappingHours = overlappingTasks.reduce((sum, t) => {
    const proRated = calculateProRatedHoursForPeriod(
      {
        start_date: t.start_date!,
        end_date: t.end_date!,
        estimated_hours: Math.max(0, (t.estimated_hours || 0) - (t.actual_hours || 0)),
      },
      effectiveStart,
      endDate,
      workingDays
    );
    return sum + proRated;
  }, 0);

  const availableForThisTask = Math.max(0, totalAvailableHours - overlappingHours);

  // Determine health status
  if (remainingHours <= 0) {
    return {
      status: 'on_track',
      message: 'Ahead of schedule',
      remainingHours: 0,
      availableHours: availableForThisTask,
      overlappingHoursFromOtherTasks: overlappingHours,
    };
  }

  if (availableForThisTask >= remainingHours) {
    return {
      status: 'on_track',
      message: `${availableForThisTask.toFixed(1)}h available`,
      remainingHours,
      availableHours: availableForThisTask,
      overlappingHoursFromOtherTasks: overlappingHours,
    };
  }

  const hoursShort = remainingHours - availableForThisTask;
  return {
    status: 'critical',
    message: `Need ${hoursShort.toFixed(1)}h more`,
    remainingHours,
    availableHours: availableForThisTask,
    overlappingHoursFromOtherTasks: overlappingHours,
  };
};

// Helper to calculate effective end date for overdue tasks
export const getEffectiveEndDateForTask = (
  task: Task,
  workingDays: string[] = DEFAULT_WORKING_DAYS,
  dailyHours: number = 8
): Date => {
  const taskEnd = task.end_date ? startOfDay(new Date(task.end_date)) : startOfDay(new Date());
  const today = startOfDay(new Date());

  if (taskEnd < today && task.phase_status !== 'done') {
    const remainingHours = Math.max(0, (task.estimated_hours || 0) - (task.actual_hours || 0));
    const daysNeeded = Math.ceil(remainingHours / dailyHours);

    if (daysNeeded > 0) {
      // Find working days from today forward
      let workingDaysCount = 0;
      let extendedDate = today;

      for (let i = 0; workingDaysCount < daysNeeded && i < 60; i++) {
        const checkDate = addDays(today, i);
        if (isWorkingDay(checkDate, workingDays)) {
          workingDaysCount++;
          extendedDate = checkDate;
        }
      }
      return extendedDate;
    }
  }

  return taskEnd;
};

export const calculateDayCapacities = (
  days: Date[],
  tasks: Task[],
  workingDays: string[] = DEFAULT_WORKING_DAYS,
  dailyHours: number = 8
): DayCapacity[] => {
  const today = startOfDay(new Date());

  return days.map((day) => {
    const isWorking = isWorkingDay(day, workingDays);
    const totalCapacity = isWorking ? dailyHours : 0;

    // Calculate hours used on this day
    const usedHours = tasks.reduce((sum, task) => {
      if (task.phase_status === 'done') return sum;
      if (!task.start_date || !task.end_date) return sum;

      const taskStart = startOfDay(new Date(task.start_date));
      const taskEnd = startOfDay(new Date(task.end_date));
      const effectiveEnd = getEffectiveEndDateForTask(task, workingDays, dailyHours);

      // For overdue tasks: distribute remaining hours from today onwards
      if (taskEnd < today) {
        // Only show hours from today forward
        if (day < today) return sum;
        if (day > effectiveEnd) return sum;

        const remainingHours = Math.max(0, (task.estimated_hours || 0) - (task.actual_hours || 0));
        const daysNeeded = Math.ceil(remainingHours / dailyHours);

        // Count working days from today to this day
        const daysFromToday = eachDayOfInterval({ start: today, end: day });
        const workingDayPosition = daysFromToday.filter(d => isWorkingDay(d, workingDays)).length - 1;

        if (workingDayPosition >= 0 && workingDayPosition < daysNeeded) {
          const hoursAlreadyDistributed = workingDayPosition * dailyHours;
          const hoursForThisDay = Math.min(dailyHours, remainingHours - hoursAlreadyDistributed);
          return sum + Math.max(0, hoursForThisDay);
        }
        return sum;
      }

      // Check if task spans this day (using original dates for non-overdue)
      if (day < taskStart || day > taskEnd) return sum;

      // Pro-rate hours for this day
      const taskWorkingDays = countWorkingDays(taskStart, taskEnd, workingDays);
      if (taskWorkingDays <= 0) return sum;

      const estimatedRemaining = Math.max(0, (task.estimated_hours || 0) - (task.actual_hours || 0));
      const hoursPerDay = estimatedRemaining / taskWorkingDays;

      return sum + (isWorking ? hoursPerDay : 0);
    }, 0);

    return {
      date: day,
      isWorkingDay: isWorking,
      totalCapacity,
      usedHours,
      utilizationPercentage: totalCapacity > 0 ? (usedHours / totalCapacity) * 100 : 0,
    };
  });
};

export const assignTaskRows = (
  tasks: Task[],
  periodDays: Date[],
  workingDays: string[] = DEFAULT_WORKING_DAYS,
  dailyHours: number = 8
): Map<string, number> => {
  const taskRows = new Map<string, number>();
  const rowEndDates: Date[] = [];
  const periodStart = periodDays[0];
  const periodEnd = periodDays[periodDays.length - 1];

  // Helper to get effective end date (for overdue tasks, calculate virtual end based on remaining hours)
  const getEffectiveEndDate = (task: Task): Date => {
    const effectiveEnd = getEffectiveEndDateForTask(task, workingDays, dailyHours);
    // Clamp to period
    return min([effectiveEnd, periodEnd]);
  };

  // Helper to get effective start date (original start, clamped to period)
  const getEffectiveStartDate = (task: Task): Date => {
    const taskStart = task.start_date ? startOfDay(new Date(task.start_date)) : startOfDay(new Date());
    return max([taskStart, periodStart]);
  };

  // Sort tasks by effective start date, then by duration (longer first)
  const sortedTasks = [...tasks].sort((a, b) => {
    const aStart = getEffectiveStartDate(a).getTime();
    const bStart = getEffectiveStartDate(b).getTime();
    if (aStart !== bStart) return aStart - bStart;

    const aEnd = getEffectiveEndDate(a).getTime();
    const bEnd = getEffectiveEndDate(b).getTime();
    const aDuration = aEnd - aStart;
    const bDuration = bEnd - bStart;
    return bDuration - aDuration;
  });

  sortedTasks.forEach(task => {
    const effectiveStart = getEffectiveStartDate(task);
    const effectiveEnd = getEffectiveEndDate(task);

    // Find the first row where task can fit
    let assignedRow = 0;
    for (let row = 0; row < rowEndDates.length; row++) {
      if (rowEndDates[row] < effectiveStart) {
        assignedRow = row;
        break;
      }
      assignedRow = row + 1;
    }

    taskRows.set(task.id, assignedRow);
    rowEndDates[assignedRow] = effectiveEnd;
  });

  return taskRows;
};

export const getTaskPosition = (
  task: Task,
  periodDays: Date[],
  workingDays: string[] = DEFAULT_WORKING_DAYS,
  dailyHours: number = 8
): { startColumn: number; endColumn: number; spanDays: number } => {
  const taskStart = task.start_date ? startOfDay(new Date(task.start_date)) : startOfDay(new Date());
  const periodStart = periodDays[0];
  const periodEnd = periodDays[periodDays.length - 1];

  // Calculate effective end date (extended for overdue tasks)
  const effectiveEnd = getEffectiveEndDateForTask(task, workingDays, dailyHours);

  // Check if task overlaps with period at all
  if (taskStart > periodEnd || effectiveEnd < periodStart) {
    return { startColumn: 0, endColumn: 0, spanDays: 0 };
  }

  // Clamp to period boundaries
  const visibleStart = max([taskStart, periodStart]);
  const visibleEnd = min([effectiveEnd, periodEnd]);

  const startColumn = periodDays.findIndex(d => isSameDay(d, visibleStart));
  const endColumn = periodDays.findIndex(d => isSameDay(d, visibleEnd));

  return {
    startColumn: Math.max(0, startColumn),
    endColumn: Math.max(0, endColumn),
    spanDays: Math.max(1, endColumn - startColumn + 1),
  };
};
